// 영문 콘텐츠 — ko.js 와 같은 사실을 영어로 쓴다 (원고: portfolio-research/v3/content/*.en.md)
import casesEn from './cases.en.js';

const en = {
  lang: 'en',
  nav: {
    home: 'Intro',
    about: 'About',
    skills: 'Skills',
    projects: 'Projects',
    build: 'How I Build',
    resume: { label: 'Career description', href: '/files/career-description-en.pdf' },
    portfolio: { label: 'Portfolio (PDF)', href: '/files/portfolio-en.pdf' },
    langSwitchLabel: 'KO',
    koOnlyNote: 'Korean only',
  },
  hero: {
    name: 'Jinhee Mok',
    title: 'AI Engineer',
    headline: 'I build AI work systems that staff use every day, and design them so people make the final call.',
    intro:
      "PL in StyleSeller's business team. I connect AI to KakaoTalk, email, and the internal work tool, and I decide how far each one is switched on and how it is verified.",
    company:
      'StyleSeller is a commerce platform that operates sales for creator sellers and brands.',
    metrics: [
      { value: 'Hundreds', label: 'of counterparts tracked stage by stage on the status board' },
      { value: '100+', label: 'shared skills used by all agents' },
      { value: '13', label: 'knowledge-hub workspaces shared by 4 agents' },
      { value: '11', label: 'staff PCs deployed with a one-line install' },
    ],
    glossaryTitle: 'Terms',
    glossary: [
      { term: 'Group buy (공구)', desc: "A time-limited sale run by an influencer, who sells a brand's products to followers for a set period" },
      { term: 'Influencer seller (셀러)', desc: 'A social media influencer who runs group buys' },
      { term: 'Brand (supplier)', desc: 'A company that supplies the products' },
      { term: 'Sales partner', desc: 'A company that runs group buys with its own sellers. Labeled "vendor" (벤더사) in the work tool UI' },
      { term: 'Vendor', desc: 'In the vendor support case, this means brands and sales partners together' },
      { term: 'KakaoTalk', desc: "Korea's dominant messenger. Personal chats have no official API" },
    ],
    casesTitle: 'Seven StyleSeller cases',
    glanceTitle: 'At a glance',
    glanceIntro:
      'I work as a PL (project lead) in the business team at StyleSeller, a commerce platform that operates sales for creator sellers and brands. I move the counterpart record-keeping and replies that staff used to repeat by hand into AI agents (AI that uses tools directly) and automation, and I run them. As of 2026-10-24, the status board shows hundreds of counterparts stage by stage, from first contact to settlement, and the vendor CS chatbot answers only the questions it found evidence for. Claude Code writes much of the code; what to build, how much of it stays switched on, and whether the results are right are mine to decide and check. The Agent Platform case also covers a follow-up improvement, the Jev skill router upgrade (90.15% on 132 questions, about 1/10 the cost).',
  },
  systemMap: {
    title: 'System Map',
    intro:
      'How the 7 cases connect. Work arrives through KakaoTalk rooms, mailboxes, Slack, and the work tool, agents handle it, and shared knowledge and staff PCs support them. Staff approve anything that writes, sends, or publishes. Click a number to jump to that case.',
    image: '/images/v3/system-map-en.png',
    zoomHint: 'Tap to enlarge',
    imageAlt: 'Map of the StyleSeller operating system: inputs, agents, knowledge, staff PCs, and human approval across the 7 cases',
    reposTitle: 'What each repository does',
    repos: [
      { name: 'Work tool repository', role: 'The work tool staff use; sales partner and product data' },
      { name: 'Agent platform repository', role: 'Agents, servers, and staff PC deployment' },
      { name: 'Email agent repository', role: 'Reply drafts for the shared mailboxes + internal knowledge hub' },
      { name: 'KakaoTalk collect-and-send tool (kakaocli fork)', role: 'KakaoTalk collection and sending on staff Macs' },
      { name: 'n8n (self-hosted, not a repository)', role: 'Triggers for mail intake, product sync calls, and match judging' },
    ],
    examples: [
      'Example: one vendor support message goes from a staff Mac (collection) → the agent server (should we answer this partner?) → the work tool (the list of partners to serve) and the knowledge hub (search), then back to a staff Mac to be sent.',
      'Example: one Spark question goes from the work tool → the agent server → the work tool API, the work databases, and knowledge hub search.',
      'Example: the status board summarizes records from KakaoTalk rooms, the order mailbox, Slack, and emailed order sheets by day, links them to a counterpart by fixed rules, and puts them on that counterpart\'s calendar. Staff resolve what it cannot link.',
    ],
    nodes: [
      { id: 'status-board', label: '① Status board · ledger', caseId: 'status-board' },
      { id: 'vendor-cs', label: '② Vendor CS agent', caseId: 'vendor-cs' },
      { id: 'ss-worktool', label: '③ Internal work tool', caseId: 'ss-worktool' },
      { id: 'email-agent', label: '④ LightRAG knowledge hub · email agent', caseId: 'email-agent' },
      { id: 'agent-platform', label: '⑤ Agent platform · Spark', caseId: 'agent-platform' },
      { id: 'content-autopilot', label: '⑥ Content autopilot', caseId: 'content-autopilot' },
      { id: 'field-fleet', label: '⑦ Staff PC deployment · operations', caseId: 'field-fleet' },
    ],
    layers: [
      {
        name: 'People',
        desc: 'The work tool (status board, pricing, matching, campaign, and Spark screens) · the Kakao CS Copilot extension (MV3) · Slack (briefings and summaries). Human control sits in three places: approval cards inside Spark chat (the approval record is rechecked in the database right before execution), a hold queue for personalized KakaoTalk campaigns with 500 or more recipients, and a per-room allowlist for the vendor support chatbot.',
      },
      {
        name: 'Agents',
        desc: 'I forked Paperclip, an open-source agent platform, and built Work OS, Spark, and Tool Registry (an execution gate) on top of it. Work that people hand off reaches the CEO agent as a work order, and an overall orchestrator reporting to the CEO (the Hermes dispatcher) hands it out to department heads and workers such as the Supervisor and fix agents. Work routed to the content department is picked up by the content orchestrator, a separate service. For the skill, Jev scores every one of the 100-odd shared skills and narrows them to the top 5, and Gemini makes the final pick of 1. If Jev fails, Gemini picks directly from the full list. What runs every day is status board collection and summaries, vendor support collection and logging, influencer CS, Spark, and monitoring and briefing services. Model calls go through a single LiteLLM proxy.',
      },
      {
        name: 'Knowledge',
        desc: 'LightRAG (Neo4j + pgvector) with 13 workspaces (in the email agent repository). Vendor support reaches it through a knowledge MCP server, Spark calls knowledge hub search directly, and work tool sales partner matching uses vector (naive) search.',
      },
      {
        name: 'Field',
        desc: 'The seller collector program runs on 11 staff PCs (Mac and Windows), with a one-line install and auto-update. Beyond that: kakaocli on Macs, a read-only collector on Windows, and Chrome extensions. Auto-update on a per-tool schedule, and a key per device.',
      },
      {
        name: 'Data and infra',
        desc: 'The business web app (Next.js) runs on Vercel; 60 containers (agent services, databases, the LLM proxy, and more) run in Docker on one GCP VM with 4 cores and 17 GB of memory (counted 2026-10-25; Compose and Swarm, self-hosted Postgres and PostgREST). Model calls go through LiteLLM, and Langfuse tracks cost. The agent platform and work tool repositories share one deploy lock.',
      },
    ],
  },
  cases: casesEn,
  howIBuild: {
    title: 'How I Build with AI',
    intro:
      'Design, build, and operations. I write code with Claude Code; architecture, rules, verification, and rollout decisions are mine.',
    introDetail:
      'Commits with a Claude co-author trailer: agent platform 3,646 · work tool 1,122 · email agent 988 (excluding merges, as of 2026-09-23; the last email agent commit was 09-02). My own commits across the four repositories total 7,276 (excluding merges, counted 2026-09-23). I run several Claude Code sessions in parallel. I assume the agents will make mistakes. The section "Where the AI was wrong and I caught it" below is the record.',
    image: null,
    loopTitle: 'Work loop',
    loop: [
      { step: 'Design doc', desc: 'I write down the problem, constraints, and alternatives first. 77 design docs (3 internal repositories combined, as of 2026-09-23).' },
      { step: 'Execution plan', desc: 'I list the steps and how to verify each one. 127 execution plans (same basis).' },
      { step: 'Test first', desc: 'I write a reproduction test, then implement.' },
      { step: 'Cross review', desc: 'Two agents from different sources review the change separately. In one commit, adversarial review caught 6 real defects (2026-09-09).' },
      { step: 'Real-device check', desc: 'I check on real PCs and real screens, and turn features on in stages, starting with a test room and one person.' },
      { step: 'Deploy check', desc: 'I deploy only images and source tagged with the deploy commit sha (never falling back to :latest), then check the running code directly after the deploy.' },
    ],
    harnessTitle: 'Harness — turning agent mistakes into rules',
    harness: [
      'CLAUDE.md and rule files: type hints, Pyright/tsc, RLS, secrets, and language rules are completion gates.',
      'Ops notes: I write down lessons so the next session doesn\'t repeat the same mistake. Example: "Green tests ≠ correct wiring."',
      'pre-commit hook: rejects hand-rolled Windows bundles with mixed line endings at commit time (2026-09-16).',
      'pre-push gate: blocks a push that changes a service unless it registers a rollback (2026-06-02). The hook has to be installed once per repository before it applies.',
      'Blocking test writes to production: a network-block guard stopped 17 tests that could have sent KakaoTalk messages to real vendors (2026-09-09).',
      'Eval set: for Spark, I write each expected answer as a SQL query and recompute it at run time. Each case runs 3 times and is judged by majority vote. The 8 cases are based on measured failures and incidents.',
    ],
    aiWrongTitle: 'Where the AI was wrong and I caught it',
    aiWrong: [
      {
        title: 'A test that never called the code (2026-09-22)',
        points: [
          'I fixed a bug where vendor support cards never closed. The test guarding that wiring checked only a hand-built dict and never called the real function.',
          'So it stayed green even when the wiring changed.',
          'I replaced it with a test that runs the real code, and added an integration test that walks the message-handling path.',
        ],
      },
      {
        title: 'A leak guard where rules overrode judgment (2026-08-18 to 08-30)',
        points: [
          'To keep other vendors\' information out of answers, I added deterministic substitution and a final gate. Then the regex started blocking on its own and skipped the LLM check.',
          'I kept the regex as a trigger only. The decision moved to an LLM check.',
        ],
      },
      {
        title: 'An analysis report with an unsupported number (2026-09-23)',
        points: [
          "A sub-agent's analysis report on the email agent repository included an uptime figure with no source.",
          'I searched the whole repository, confirmed the figure had no source, and left it out.',
        ],
      },
    ],
    incidentsTitle: 'Principles learned from incidents',
    incidents: [
      { title: 'Green tests ≠ correct wiring', lesson: 'If no test goes through a branch, even a missing import stays green. I start the real process and run it through the catch path too.' },
      { title: "Not finding it once doesn't mean it isn't there", lesson: 'Before I report that something is missing, I recheck the time range, what I searched, and the entry and exit points.' },
      { title: 'Judge by the running code', lesson: 'After a deploy, I check the code that is actually running.' },
      { title: 'No irreversible bulk conversions', lesson: 'I change code to accept both the old and new formats.' },
      { title: 'Turn things off with a switch', lesson: 'I count real usage first, then shut things down by switching them off and keeping the files.' },
    ],
  },
  about: {
    title: 'About Me',
    photo: '/images/img.jpg',
    tagline: "I'm Jinhee Mok, an AI agent engineer who turns repetitive work into AI systems.",
    paragraphs: [
      'I majored in law, later earned a business administration degree through the Academic Credit Bank System, and worked as a daycare operations manager and an English teacher. In 2024 I learned software development at HiMedia Academy, and during my We:Review internship I automated receipt review with OCR and an LLM. That became the starting point for what I do now: cutting repetitive work in the business with AI.',
      "I joined StyleSeller in November 2025. I started with seller DM automation, and now I build and run vendor support, the internal work tool, the email agent and knowledge hub, the agent platform (Spark · Work OS), and staff PC deployment. Claude Code writes a lot of my code, but I don't call anything done until it has run on a real staff PC.",
      'I turn what the business needs into rules by checking them, cell by cell, against the values staff were already using. The systems stop at candidates and suggestions; staff make the final call. When something breaks, I write down the cause and the fix, with the date.',
    ],
    nowTitle: 'Now',
    now: [
      'The vendor support chatbot answers only tagged questions in real partner rooms, without hand-off. Amounts, percentages, and brand names in each answer are checked against the evidence and rewritten when they do not match.',
      'I am adding monitoring that catches failures that stop silently. In early October I changed a health check that reported "healthy" even when the database had stopped, so it now checks the database too.',
      'I upgraded the cost tracking tool (Langfuse) to a new version (10-04).',
    ],
    timelineTitle: 'StyleSeller month by month',
    timeline: [
      { month: '2025-11', text: 'Joined, learned the team\'s work' },
      { month: '2025-12 to 2026-01', text: 'Started automating team work' },
      { month: '2026-02', text: 'First commit on Instagram DM automation (02-08), moved it to a Chrome extension (02-27)' },
      { month: '2026-03', text: 'First work tool commit — a pricing calculator and proposals moved over from a Google Sheet price sheet (03-31)' },
      { month: '2026-04', text: 'Added a LightRAG knowledge graph to the email agent (04-03); AI parsing of supplier catalogs' },
      { month: '2026-05', text: 'Started the agent platform (05-16, Paperclip fork); KakaoTalk CS Chrome extension (05-24)' },
      { month: '2026-06', text: 'Removed Claude from LiteLLM and moved to Gemini (06-06); started the vendor support backend (06-29)' },
      { month: '2026-07', text: 'Started Spark and Work OS (07-18 to 07-19); expanded the knowledge hub to multiple workspaces (07-10)' },
      { month: '2026-08', text: 'When a cloud DB quota overrun stopped the work tool API, I stood up the same API on a VM and restored service the same day (08-20); started personalized KakaoTalk campaigns (08-24)' },
      { month: '2026-09', text: 'Rolled out a one-line install to 11 staff PCs (09-16); seller-finder workers running on 9 PCs (09-18)' },
      { month: '2026-10', text: 'Stronger monitoring for silent failures; Langfuse upgrade' },
    ],
    repoPeriods: 'Active period per repository: work tool 2026-03 onward · email agent 2026-03 onward (last commit 09-02) · agent platform 2026-05 onward · kakaocli fork 2026-06 to 09 · Instagram DM extension 2026-02 onward (last commit 07-01)',
    experienceTitle: 'Journey',
    experienceLead: 'From a law major to AI agent engineer',
    careerChangeLabel: 'Career Change',
    experience: [
      { period: '2025-11 – present', org: 'StyleSeller · PL (business team)', summary: 'Internal AI and automation', kind: 'tech' },
      { period: '2025-08 – 2025-10', org: 'We:Review (Welead) · intern', summary: 'Built an OCR + LLM receipt review pipeline', kind: 'tech' },
      { period: '2025-05 – 2025-07', org: 'Korea IT Business Promotion Association', summary: 'Generative AI developer training program', kind: 'tech' },
      { period: '2024-10 – 2024-12', org: 'HiMedia Academy · AI course', summary: 'AI projects with industry mentors (LangChain RAG chatbot, OCR and translation pipeline)', kind: 'tech' },
      { period: '2024-04 – 2024-10', org: 'HiMedia Academy · full-stack course', summary: 'Spring Boot and Vue.js team projects; collaboration and version control', kind: 'tech', careerChange: true },
      { period: '2021-02 – 2023-06', org: 'Academic Credit Bank System · Business Administration', summary: 'Business administration degree program', kind: 'pre' },
      { period: '2018-08 – 2024-02', org: 'Monte Kids · Btop Kinder · Jungsang Academy · English teacher', summary: 'Lesson design, teaching materials, and parent consultations', kind: 'pre' },
      { period: '2015-03 – 2018-02', org: 'Jeonwon Daycare Center · operations manager', summary: 'Staff management and day-to-day operations', kind: 'pre' },
      { period: '2008-03 – 2012-02', org: 'Cheongju University · B.A. in Law', summary: 'Law major', kind: 'pre' },
    ],
  },
  skills: {
    title: 'Skills',
    lead: 'What I use in production, listed separately from what I used in training programs.',
    sections: [
      {
        id: 'production',
        title: 'Used in production (StyleSeller, 2026-02 onward)',
        groups: [
          {
            id: 'ai-agent',
            title: 'AI / Agent',
            items: [
              { id: 'llm-agent', label: 'Paperclip · Hermes agents' },
              { id: 'jev', label: 'Jev skill recommender' },
              { id: 'hitl', label: 'Tool execution approval gate' },
              { id: 'litellm', label: 'Gemini Flash family (via LiteLLM)' },
              { id: 'mcp', label: '4 MCP servers · tool calling' },
              { id: 'claude-code', label: 'Claude Code' },
            ],
          },
          {
            id: 'rag-llmops',
            title: 'RAG · LLM Ops',
            items: [
              { id: 'rag', label: 'LightRAG 1.5.6 knowledge hub (Neo4j · pgvector)' },
              { id: 'litellm-cap', label: 'LiteLLM spend cap' },
              { id: 'langfuse', label: 'Langfuse 4.50 tracing' },
            ],
          },
          {
            id: 'backend',
            title: 'Backend',
            items: [
              { id: 'python', label: 'Python 3.12 · FastAPI · Pydantic v2' },
              { id: 'ts', label: 'TypeScript' },
              { id: 'nextjs', label: 'Next.js 16.2' },
              { id: 'node', label: 'Node.js · Express 5' },
            ],
          },
          {
            id: 'db',
            title: 'DB',
            items: [
              { id: 'postgres', label: 'Self-hosted Postgres + PostgREST' },
              { id: 'rls', label: 'RLS · read-only roles' },
              { id: 'audit-log', label: 'Append-only audit log' },
            ],
          },
          {
            id: 'field-infra',
            title: 'Staff PCs · infra',
            items: [
              { id: 'launchd', label: 'One-line install on 11 staff Macs and Windows PCs' },
              { id: 'device-keys', label: 'Per-device keys' },
              { id: 'nginx', label: 'VM · nginx' },
              { id: 'docker', label: 'Commit-pinned image deploys' },
              { id: 'chrome-ext', label: 'Chrome Extension MV3' },
              { id: 'playwright', label: 'Playwright · CDP' },
              { id: 'swift', label: 'Swift (kakaocli fork)' },
              { id: 'n8n', label: 'n8n' },
              { id: 'gh-actions', label: 'GitHub Actions' },
              { id: 'gcp', label: 'GCP VM' },
            ],
          },
        ],
      },
      {
        id: 'training',
        title: 'Used in training programs (2024)',
        groups: [
          {
            id: 'fullstack-course',
            title: 'Full-stack course',
            items: [
              { id: 'java', label: 'Java' },
              { id: 'spring', label: 'Spring Boot' },
              { id: 'mysql', label: 'MySQL' },
              { id: 'jpa', label: 'JPA' },
              { id: 'vue', label: 'Vue.js' },
              { id: 'flutter', label: 'Flutter' },
            ],
          },
          {
            id: 'ai-course',
            title: 'AI course',
            items: [
              { id: 'rag-course', label: 'LangChain RAG chatbot' },
              { id: 'model-pipeline', label: 'OCR and translation pipeline (Tesseract, T5, NLLB)' },
            ],
          },
        ],
      },
    ],
  },
};

export default en;
